import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Gift, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useColorTheme } from '../contexts/ColorThemeContext';
import { giftService } from '../services/giftService';
import { cn } from '../util/designUtil';
import {
  GiftFailure,
  MIN_GIFT_CREDITS,
  QUICK_GIFT_AMOUNTS,
  formatGiftSent,
  formatGiftSpend,
  giftFailure,
  parseGiftAmount,
} from '../util/giftAmount';

interface SendGiftModalProps {
  creatorId: string;
  creatorName: string;
  onClose: () => void;
  onSent: (credits: number) => void;
}

/** How long the confirmation stays up before the modal closes itself. */
const CONFIRMATION_MS = 1500;

/**
 * The "send a gift" form: quick amounts, a free-text credits field and a
 * Confirm that only ever sends a whole number of credits.
 *
 * The response carries the creator's share, and none of it is read here: the
 * fan sees what they spend and nothing about earnings or commission.
 */
const SendGiftModal: React.FC<SendGiftModalProps> = ({
  creatorId,
  creatorName,
  onClose,
  onSent,
}) => {
  const { theme } = useColorTheme();

  const [amount, setAmount] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const [failure, setFailure] = useState<GiftFailure | null>(null);
  const [sending, setSending] = useState(false);
  const [sentCredits, setSentCredits] = useState<number | null>(null);

  // One gift in flight at a time: state updates land a render too late to stop
  // a second click, so the guard has to be a ref.
  const sendingRef = useRef(false);
  const mountedRef = useRef(true);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (closeTimerRef.current !== null) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, []);

  const handleClose = useCallback(() => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(async () => {
    if (sendingRef.current) {
      return;
    }

    const parsed = parseGiftAmount(amount);
    if (!parsed.ok) {
      // Nothing goes on the wire: the backend would only answer 400.
      setInputError(parsed.reason);
      setFailure(null);
      return;
    }

    sendingRef.current = true;
    setSending(true);
    setInputError(null);
    setFailure(null);

    const result = await giftService.sendGift(creatorId, parsed.credits);

    if (!mountedRef.current) {
      return;
    }
    sendingRef.current = false;

    if (result.success) {
      const credits = result.amountCredits ?? parsed.credits;
      setSentCredits(credits);
      onSent(credits);
      toast.success(formatGiftSent(credits, creatorName));
      closeTimerRef.current = setTimeout(() => {
        closeTimerRef.current = null;
        onClose();
      }, CONFIRMATION_MS);
    } else {
      // The typed amount is left alone, so the fan can top up and retry.
      setFailure(giftFailure(result.status, result.message));
    }

    setSending(false);
  }, [amount, creatorId, creatorName, onClose, onSent]);

  const parsedAmount = parseGiftAmount(amount);

  return (
    <div
      className="gift-modal-backdrop fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto"
      onClick={e => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="sendGiftHeading"
        className="relative bg-white rounded-lg p-4 sm:p-6 w-full max-w-sm my-4 shadow-xl"
      >
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          className="absolute top-3 right-3 p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
        >
          <X className="h-4 w-4" />
        </button>

        <h2
          id="sendGiftHeading"
          className="flex items-start gap-2 pr-8 text-base font-semibold text-gray-900 break-words"
        >
          <Gift className={cn('h-5 w-5 shrink-0 mt-0.5', theme.secondary)} />
          <span className="min-w-0 break-words">Send a gift to {creatorName}</span>
        </h2>

        {sentCredits !== null ? (
          <div className="mt-4 flex flex-col gap-3">
            <p className="flex items-start gap-2 text-sm font-medium text-gray-900 break-words">
              <Check className={cn('h-5 w-5 shrink-0 mt-0.5', theme.secondary)} />
              <span className="min-w-0 break-words">
                {formatGiftSent(sentCredits, creatorName)}
              </span>
            </p>
            <button
              type="button"
              onClick={handleClose}
              className={cn(
                'w-full inline-flex items-center justify-center px-4 py-2 text-white rounded-md text-sm font-semibold',
                theme.accent,
              )}
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <p className="mt-1 text-xs text-gray-600 break-words">
              A one-off gift of credits. This is not a subscription.
            </p>

            <div className="mt-4 grid grid-cols-4 gap-2">
              {QUICK_GIFT_AMOUNTS.map(quick => (
                <button
                  key={quick}
                  type="button"
                  onClick={() => {
                    setAmount(String(quick));
                    setInputError(null);
                    setFailure(null);
                  }}
                  className={cn(
                    'min-w-0 px-2 py-2 rounded-md text-sm font-semibold border',
                    amount.trim() === String(quick)
                      ? cn('text-white border-transparent', theme.accent)
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
                  )}
                >
                  {quick}
                </button>
              ))}
            </div>

            <label
              htmlFor="giftAmountCredits"
              className="block mt-4 text-xs font-medium text-gray-700 mb-1"
            >
              Amount (credits)
            </label>
            <input
              id="giftAmountCredits"
              type="text"
              inputMode="numeric"
              value={amount}
              onChange={e => {
                setAmount(e.target.value);
                setInputError(null);
                setFailure(null);
              }}
              placeholder={`e.g. ${QUICK_GIFT_AMOUNTS[1]}`}
              className={cn(
                'w-full min-w-0 border rounded-md px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500',
                inputError ? 'border-red-400' : 'border-gray-300',
              )}
            />

            {inputError ? (
              <p className="mt-2 text-xs text-red-600 break-words">{inputError}</p>
            ) : (
              <p className="mt-2 text-xs text-gray-600 break-words">
                {parsedAmount.ok
                  ? formatGiftSpend(parsedAmount.credits)
                  : `Whole credits only, at least ${MIN_GIFT_CREDITS}.`}
              </p>
            )}

            {failure && (
              <p className="mt-3 text-xs text-red-600 break-words">
                {failure.message}
                {failure.showBuyCredits && (
                  <>
                    {' '}
                    <Link
                      to="/buy-credits"
                      className={cn('font-semibold underline', theme.secondary)}
                    >
                      Buy credits
                    </Link>
                  </>
                )}
              </p>
            )}

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleClose}
                disabled={sending}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleConfirm()}
                disabled={sending}
                className={cn(
                  'w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 text-white rounded-md text-sm font-semibold disabled:opacity-50',
                  theme.accent,
                )}
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Sending…
                  </>
                ) : (
                  'Confirm'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SendGiftModal;
